import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/Card';

const API_BASE = 'https://api.glowytics.com';

// Convert Meta product set rules (often JSON or double-escaped JSON) into a readable summary
function parseMaybeJson(input) {
  if (!input) return null;
  if (typeof input === 'object') return input;
  if (typeof input !== 'string') return null;
  const raw = input.trim();
  const attempts = [
    (s) => JSON.parse(s),
    (s) => JSON.parse(s.replace(/\\"/g, '"')),
    (s) => {
      const once = JSON.parse(s);
      return typeof once === 'string' ? JSON.parse(once) : once;
    },
  ];
  for (const attempt of attempts) {
    try {
      const parsed = attempt(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      // continue
    }
  }
  return null;
}

function summarizeRulesObject(rulesObject) {
  const summary = {
    availability: null,
    productTypeContains: [],
    retailerIds: [],
    retailerGroupIds: [],
    others: [],
  };

  function handleCondition(key, valueObj) {
    if (!valueObj || typeof valueObj !== 'object') return;
    const operators = Object.keys(valueObj);
    if (operators.length === 0) return;
    const op = operators[0];
    const val = valueObj[op];

    // Normalize known keys
    if (key === 'availability' && op === 'eq') {
      summary.availability = String(val);
      return;
    }
    if (key === 'product_type' && (op === 'i_contains' || op === 'contains')) {
      summary.productTypeContains.push(String(val));
      return;
    }
    if (key === 'retailer_id' && op === 'eq') {
      summary.retailerIds.push(String(val));
      return;
    }
    if (key === 'retailer_product_group_id' && op === 'eq') {
      summary.retailerGroupIds.push(String(val));
      return;
    }

    // Generic fallback
    summary.others.push({ key, op, val });
  }

  function traverse(node) {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(traverse);
      return;
    }
    if (node.or && Array.isArray(node.or)) {
      node.or.forEach(traverse);
      return;
    }
    if (node.and && Array.isArray(node.and)) {
      node.and.forEach(traverse);
      return;
    }
    // Leaf conditions object
    Object.entries(node).forEach(([k, v]) => handleCondition(k, v));
  }

  traverse(rulesObject);
  return summary;
}

function formatSummaryLines(summary) {
  const lines = [];
  const capList = (arr, cap = 8) => {
    if (arr.length <= cap) return arr.join(', ');
    const shown = arr.slice(0, cap).join(', ');
    return `${shown}, +${arr.length - cap} more`;
    }
  ;
  if (summary.availability) {
    lines.push(`availability equals "${summary.availability}"`);
  }
  if (summary.productTypeContains.length > 0) {
    lines.push(
      `product_type contains ${summary.productTypeContains
        .map((v) => `"${v}"`)
        .join(', ')}`
    );
  }
  if (summary.retailerGroupIds.length > 0) {
    lines.push(
      `retailer_product_group_id equals ${capList(summary.retailerGroupIds)}`
    );
  }
  if (summary.retailerIds.length > 0) {
    lines.push(`retailer_id equals ${capList(summary.retailerIds)}`);
  }
  summary.others.forEach(({ key, op, val }) => {
    const valueText =
      Array.isArray(val) ? `[${val.join(', ')}]` : typeof val === 'object' ? JSON.stringify(val) : String(val);
    lines.push(`${key} ${op} ${valueText}`);
  });
  return lines;
}

function RuleSummary({ rules }) {
  const parsed = parseMaybeJson(rules);
  if (!parsed) {
    return <span className="text-xs text-gray-400">No rule</span>;
  }
  const summary = summarizeRulesObject(parsed);

  const RuleChip = ({ children, variant = 'gray' }) => {
    const styles =
      variant === 'green'
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : variant === 'purple'
        ? 'bg-purple-50 text-purple-700 border-purple-200'
        : variant === 'blue'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-gray-50 text-gray-700 border-gray-200';
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-xl text-[11px] font-medium border w-full text-left break-words whitespace-pre-wrap ${styles}`}>
        {children}
      </span>
    );
  };

  // Build chip list
  const chips = [];
  if (summary.availability) {
    chips.push(
      <RuleChip key="availability" variant="green">
        availability = "{summary.availability}"
      </RuleChip>
    );
  }
  if (summary.productTypeContains.length > 0) {
    summary.productTypeContains.slice(0, 6).forEach((v, i) => {
      chips.push(
        <RuleChip key={`pt-${i}`} variant="purple">
          product_type contains "{v}"
        </RuleChip>
      );
    });
    if (summary.productTypeContains.length > 6) {
      chips.push(
        <RuleChip key="pt-more" variant="purple">
          +{summary.productTypeContains.length - 6} more
        </RuleChip>
      );
    }
  }
  if (summary.retailerGroupIds.length > 0) {
    summary.retailerGroupIds.slice(0, 8).forEach((v, i) => {
      chips.push(
        <RuleChip key={`rpg-${i}`} variant="blue">
          group_id: {v}
        </RuleChip>
      );
    });
    if (summary.retailerGroupIds.length > 8) {
      chips.push(
        <RuleChip key="rpg-more" variant="blue">
          +{summary.retailerGroupIds.length - 8} more
        </RuleChip>
      );
    }
  }
  if (summary.retailerIds.length > 0) {
    summary.retailerIds.slice(0, 8).forEach((v, i) => {
      chips.push(
        <RuleChip key={`rid-${i}`} variant="blue">
          retailer_id: {v}
        </RuleChip>
      );
    });
    if (summary.retailerIds.length > 8) {
      chips.push(
        <RuleChip key="rid-more" variant="blue">
          +{summary.retailerIds.length - 8} more
        </RuleChip>
      );
    }
  }
  if (summary.others.length > 0) {
    summary.others.slice(0, 6).forEach((o, i) => {
      const variant = o.key.includes('price') ? 'green' : o.key.includes('type') ? 'purple' : 'gray';
      chips.push(
        <RuleChip key={`oth-${i}`} variant={variant}>
          {o.key} {o.op} {Array.isArray(o.val) ? `[${o.val.join(', ')}]` : typeof o.val === 'object' ? JSON.stringify(o.val) : String(o.val)}
        </RuleChip>
      );
    });
    if (summary.others.length > 6) {
      chips.push(
        <RuleChip key="oth-more">
          +{summary.others.length - 6} more
        </RuleChip>
      );
    }
  }

  if (chips.length === 0) {
    return <span className="text-xs text-gray-400">No rule</span>;
  }

  // Cloud-like v-card container
  return (
    <div className="mt-2 p-3 rounded-2xl bg-white border border-gray-200 shadow-sm w-full">
      <div className="flex flex-col gap-2">
        {chips}
      </div>
    </div>
  );
}

export default function MetaCatalogTest() {
  const { selectedBrand } = useAuth();
  const [catalogId, setCatalogId] = useState('');
  const [catalogName, setCatalogName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [productSets, setProductSets] = useState([]);
  const [loadingSets, setLoadingSets] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newSetName, setNewSetName] = useState('glowytics');
  const [newSetRetailerId, setNewSetRetailerId] = useState('108234');
  const [errorDetails, setErrorDetails] = useState(null);
  const [customRulesJson, setCustomRulesJson] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [lastCreateInfo, setLastCreateInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState('');

  const canRun = !!selectedBrand?.id && !!catalogId;

  const resolveCatalogFromConnections = async () => {
    if (!selectedBrand?.id) return;
    setError('');
    setResult(null);
    setCatalogId('');
    setCatalogName('');
    try {
      const resp = await fetch(`${API_BASE}/api/auth/connections`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      // data could be array; find META_CATALOG
      const connections = Array.isArray(data) ? data : (Array.isArray(data?.connections) ? data.connections : []);
      const metaCatalogConn = connections.find((c) => c?.platform === 'META_CATALOG' && c?.status === 'ACTIVE');
      if (!metaCatalogConn) {
        throw new Error('Active META_CATALOG connection not found. Please connect a catalog first.');
      }
      const resolvedCatalogId = metaCatalogConn.customerId || metaCatalogConn.catalogId || '';
      const resolvedCatalogName = metaCatalogConn.accountName || metaCatalogConn.catalogName || '';
      if (!resolvedCatalogId) {
        throw new Error('Catalog ID not found in saved connection.');
      }
      setCatalogId(resolvedCatalogId);
      setCatalogName(resolvedCatalogName);
    } catch (e) {
      setError(e.message || 'Bağlantılar alınamadı');
    }
  };

  const fetchDetails = async () => {
    if (!canRun) return;
    setLoading(true);
    setLoadingSets(true);
    setError('');
    setResult(null);
    setProductSets([]);
    try {
      const [detailsResp, setsResp] = await Promise.all([
        fetch(`${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId.trim())}/details`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': selectedBrand.id
          }
        }),
        fetch(`${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId.trim())}/product-sets`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Brand-ID': selectedBrand.id
          }
        })
      ]);

      if (!detailsResp.ok) {
        const data = await detailsResp.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${detailsResp.status}`);
      }
      if (!setsResp.ok) {
        const data = await setsResp.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${setsResp.status}`);
      }

      const detailsData = await detailsResp.json();
      const setsData = await setsResp.json();
      setResult(detailsData?.data || null);
      setProductSets(Array.isArray(setsData?.data) ? setsData.data : []);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
      setLoadingSets(false);
    }
  };

  const createProductSet = async () => {
    if (!selectedBrand?.id || !catalogId) return;
    setCreateLoading(true);
    setError('');
    setErrorDetails(null);
    setLastCreateInfo(null);
    try {
      // If custom rules provided, validate JSON locally first
      let body = {
        name: newSetName || 'glowytics',
      };
      if (customRulesJson && customRulesJson.trim()) {
        try {
          const parsed = JSON.parse(customRulesJson);
          body.filter = parsed;
        } catch (e) {
          setError('Invalid JSON. Please provide valid rules.');
          setErrorDetails({ parseError: e.message });
          return;
        }
      } else {
        body.retailerId = newSetRetailerId?.toString().trim() || '108234';
      }

      const resp = await fetch(`${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId.trim())}/product-sets`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify(body)
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError(data?.error || data?.details || `HTTP ${resp.status}`);
        setErrorDetails(data || null);
        return;
      }
      const data = await resp.json().catch(() => ({}));
      setLastCreateInfo(data || { ok: true });
      // Creation successful: refresh product sets
      await fetchDetails();
      setIsCreateOpen(false);
    } catch (e) {
      setError(e.message || 'Failed to create product set');
    } finally {
      setCreateLoading(false);
    }
  };

  useEffect(() => {
    resolveCatalogFromConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  // Auto-fetch details as soon as catalog is resolved
  useEffect(() => {
    if (canRun) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRun, catalogId]);

  const fetchProducts = async () => {
    if (!selectedBrand?.id || !catalogId) return;
    setLoadingProducts(true);
    setProductsError('');
    setProducts([]);
    try {
      const resp = await fetch(`${API_BASE}/api/meta-ads/catalogs/${encodeURIComponent(catalogId.trim())}/products?limit=50`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setProducts(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setProductsError(e.message || 'Failed to fetch products');
    } finally {
      setLoadingProducts(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <Card className="p-5 rounded-2xl shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
              <img src="/6033716.png" alt="Meta" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Meta Catalog</h1>
              <p className="text-sm text-gray-500 mt-1">Manage your product sets and track the catalog summary.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resolveCatalogFromConnections}
              disabled={loading}
              className={`px-4 py-2 rounded-xl border ${
                loading ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
              }`}
            >
              Rescan Catalog
            </button>
            <button
              onClick={fetchProducts}
              disabled={!canRun || loadingProducts}
              className={`px-4 py-2 rounded-xl text-white ${
                !canRun || loadingProducts ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loadingProducts ? 'Loading products…' : 'Fetch Products (50)'}
            </button>
          </div>
        </div>
        {!selectedBrand?.id && <div className="mt-3 text-sm text-red-500">Please select a brand.</div>}
      </Card>

      {error && (
        <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
          {errorDetails && (
            <pre className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 overflow-auto">
{JSON.stringify(errorDetails, null, 2)}
            </pre>
          )}
        </div>
      )}

      {result && (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-white">
              <p className="text-white/80 text-xs mb-1">Total Products</p>
              <p className="text-2xl font-bold">{result.products?.total ?? '—'}</p>
            </Card>
            <Card className="p-4 rounded-xl">
              <p className="text-gray-500 text-xs mb-1">Product Sets</p>
              <p className="text-2xl font-bold text-gray-900">{result.productSets?.total ?? '—'}</p>
            </Card>
            <Card className="p-4 rounded-xl">
              <p className="text-gray-500 text-xs mb-1">Total Feeds</p>
              <p className="text-2xl font-bold text-gray-900">{result.feeds?.total ?? '—'}</p>
            </Card>
            <Card className="p-4 rounded-xl">
              <p className="text-gray-500 text-xs mb-1">Catalog Name</p>
              <p className="text-base font-semibold text-gray-900 truncate">{result.name || '—'}</p>
              <p className="text-xs text-gray-500 mt-1 break-all">{result.id || '—'}</p>
            </Card>
          </div>
        </div>
      )}

      {productsError && (
        <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
          {productsError}
        </div>
      )}

      {products.length > 0 && (
        <Card className="mt-6 rounded-xl">
          <div className="p-4 border-b">
            <h2 className="font-medium text-gray-800">First 50 Products</h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {products.map((p) => (
                <div key={p.id} className="flex gap-3 p-3 border border-gray-100 rounded-lg">
                  <div className="w-20 h-20 bg-gray-50 border border-gray-200 rounded overflow-hidden flex-shrink-0">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name || p.id} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No Image</div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{p.name || '—'}</div>
                    <div className="text-xs text-gray-500 truncate">{p.retailer_id || '—'}</div>
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">
                        View product
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {result && (
        <Card className="mt-6 rounded-xl overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-medium text-gray-800">Product Sets</h2>
            <button
              onClick={() => setIsCreateOpen(true)}
              className="px-3 py-2 rounded-xl text-white bg-emerald-600 hover:bg-emerald-700"
              disabled={!canRun}
            >
              New Product Set
            </button>
          </div>
          {loadingSets ? (
            <div className="p-4 text-sm text-gray-500">Loading…</div>
          ) : productSets.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No product sets found.</div>
          ) : (
            <ul className="divide-y divide-gray-100 p-2">
              {productSets.map((s) => (
                <li key={s.id} className="py-2">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="inline-flex h-2 w-2 rounded-full bg-indigo-500 flex-shrink-0" />
                      <span className="font-medium text-gray-900 truncate">{s.name || '—'}</span>
                      <span className="text-xs text-gray-500 truncate">• {s.id}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                        {typeof s.product_count === 'number' ? `${s.product_count} products` : '—'}
                      </span>
                      <details className="ml-2">
                        <summary className="cursor-pointer text-xs text-gray-600 hover:text-gray-800">View Rules</summary>
                        <RuleSummary rules={s.rules} />
                      </details>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Create Product Set Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => !createLoading && setIsCreateOpen(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">New Product Set</h3>
                  <button
                    onClick={() => setIsCreateOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                    disabled={createLoading}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={newSetName}
                      onChange={(e) => setNewSetName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">retailer_id equals</label>
                    <input
                      type="text"
                      value={newSetRetailerId}
                      onChange={(e) => setNewSetRetailerId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="mb-2 text-xs text-gray-500">
                    Alternative: If you provide valid JSON rules below (e.g. a large OR structure), retailer_id will be ignored.
                  </div>
                  <textarea
                    rows={6}
                    value={customRulesJson}
                    onChange={(e) => setCustomRulesJson(e.target.value)}
                    placeholder='Example: {"or":[{"retailer_id":{"eq":"79398"}},{"retailer_id":{"eq":"69544"}}]}'
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-xs"
                  />
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
                <button
                  onClick={() => setIsCreateOpen(false)}
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={createLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={createProductSet}
                  disabled={createLoading}
                  className={`px-4 py-2 rounded-md text-white ${createLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                >
                  {createLoading ? 'Creating…' : 'Create'}
                </button>
              </div>
              {lastCreateInfo?.data?.id && (
                <div className="px-5 pb-4">
                  <div className="mt-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                    Created: {lastCreateInfo.data.id}{lastCreateInfo?.data?.duplicate ? ' (existing set)' : ''}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

